import React, { useEffect, useState, useCallback } from 'react';
import { Save, Gift, Sparkles, AlertCircle, RotateCcw, Mail } from 'lucide-react';

import SafeHtml from './SafeHtml';
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * WelcomeBonusEditor — admin tab to configure the "Bonus for Registration"
 * feature. Three blocks:
 *   1) Numeric settings (enabled, min_deposit, bonus_amount, validity_days)
 *   2) Welcome modal (title + html). Live preview on the right.
 *   3) Pointer to the email template editor (slug=welcome_bonus) which
 *      lives in the existing Email tab — we cross-link instead of
 *      duplicating the rich editor here.
 *
 * The modal supports {{min_deposit}}, {{bonus_amount}}, {{validity_days}},
 * {{total_balance}} placeholders so the same HTML reflects whatever the
 * admin enters above without needing to be re-edited.
 */
export default function WelcomeBonusEditor({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    enabled: true,
    min_deposit: 40,
    bonus_amount: 50,
    validity_days: 30,
    modal_title: '',
    modal_html: '',
    default_modal_html: '',
    default_modal_title: '',
  });
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/config/welcome-bonus`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        if (r.status === 401) {
          // Stale admin token — typically happens when MFA/password
          // was changed in another tab and the session epoch was
          // bumped. Clear the form so the page doesn't fool the user
          // into thinking blank fields are the persisted state, and
          // surface an actionable hint.
          setData({
            enabled: false, min_deposit: 0, bonus_amount: 0, validity_days: 30,
            modal_title: '', modal_html: '',
            default_modal_html: '', default_modal_title: '',
          });
          throw new Error('Session expired — please log out and log in again to refresh your admin token.');
        }
        throw new Error(`HTTP ${r.status}`);
      }
      const j = await r.json();
      setData({
        enabled:        !!j.enabled,
        min_deposit:    Number(j.min_deposit  || 0),
        bonus_amount:   Number(j.bonus_amount || 0),
        validity_days:  Number(j.validity_days || 30),
        modal_title:    j.modal_title || '',
        modal_html:     j.modal_html  || '',
        default_modal_html:  j.default_modal_html  || '',
        default_modal_title: j.default_modal_title || '',
      });
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/config/welcome-bonus`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: data.enabled,
          min_deposit:  Number(data.min_deposit),
          bonus_amount: Number(data.bonus_amount),
          validity_days: Number(data.validity_days),
          modal_title: data.modal_title,
          modal_html:  data.modal_html,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setData((d) => ({ ...d, ...j }));
      alert('Welcome bonus settings saved.');
    } catch (e) { setError(e.message || 'Save failed'); alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  const restoreDefaults = () => {
    if (!window.confirm('Restore default modal title and HTML? Numeric settings keep their current values.')) return;
    setData((d) => ({ ...d, modal_title: d.default_modal_title || '', modal_html: d.default_modal_html || '' }));
  };

  // Render preview by replacing {{placeholders}} with current numeric values.
  const previewHtml = (() => {
    const total = (Number(data.min_deposit) || 0) + (Number(data.bonus_amount) || 0);
    const fmt = (n) => Number.isInteger(n) ? String(n) : n.toFixed(2);
    return (data.modal_html || '')
      .replaceAll('{{min_deposit}}',   fmt(Number(data.min_deposit)  || 0))
      .replaceAll('{{bonus_amount}}',  fmt(Number(data.bonus_amount) || 0))
      .replaceAll('{{validity_days}}', String(Number(data.validity_days) || 0))
      .replaceAll('{{total_balance}}', fmt(total));
  })();

  if (loading) {
    return <div className="p-10 text-center text-slate-500" data-testid="wb-loading">Loading welcome-bonus settings…</div>;
  }

  return (
    <div className="space-y-6" data-testid="welcome-bonus-editor">
      <div className="bg-gradient-to-r from-violet-50 to-cyan-50 border border-violet-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-500 text-white flex items-center justify-center flex-shrink-0">
            <Gift className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-bold text-slate-800">Bonus for Registration</h2>
            <p className="text-sm text-slate-600">
              Award every <strong>newly-verified</strong> user a one-time bonus that auto-credits on their first deposit.
              Migrated/legacy users and password-reset flows are excluded automatically.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm" data-testid="wb-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Numeric settings block */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-600" /> Numeric Settings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Enabled</span>
            <button
              type="button"
              onClick={() => setData((d) => ({ ...d, enabled: !d.enabled }))}
              data-testid="wb-toggle-enabled"
              className={`px-3 py-2 rounded-lg font-bold text-sm transition-all ${data.enabled ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}
            >
              {data.enabled ? 'ON' : 'OFF'}
            </button>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Min first deposit (€)</span>
            <input
              type="number" min="0" step="1"
              value={data.min_deposit}
              onChange={(e) => setData((d) => ({ ...d, min_deposit: e.target.value }))}
              data-testid="wb-min-deposit"
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Bonus amount (€)</span>
            <input
              type="number" min="0" step="1"
              value={data.bonus_amount}
              onChange={(e) => setData((d) => ({ ...d, bonus_amount: e.target.value }))}
              data-testid="wb-bonus-amount"
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Valid for (days)</span>
            <input
              type="number" min="1" step="1"
              value={data.validity_days}
              onChange={(e) => setData((d) => ({ ...d, validity_days: e.target.value }))}
              data-testid="wb-validity-days"
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono"
            />
          </label>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Example: deposit <strong>€{Number(data.min_deposit) || 0}</strong>, the customer gets <strong>€{Number(data.bonus_amount) || 0}</strong> bonus → total <strong>€{(Number(data.min_deposit)||0) + (Number(data.bonus_amount)||0)}</strong>.
        </p>
      </div>

      {/* Modal block */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">Welcome modal — shown on the user's first login</h3>
          <button
            type="button"
            onClick={restoreDefaults}
            data-testid="wb-restore-defaults"
            className="px-3 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg flex items-center gap-1.5"
          >
            <RotateCcw className="w-3 h-3" /> Restore defaults
          </button>
        </div>
        <label className="flex flex-col gap-1.5 mb-3">
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Modal title</span>
          <input
            type="text"
            value={data.modal_title}
            onChange={(e) => setData((d) => ({ ...d, modal_title: e.target.value }))}
            data-testid="wb-modal-title"
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </label>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">HTML (placeholders supported)</span>
            <textarea
              value={data.modal_html}
              onChange={(e) => setData((d) => ({ ...d, modal_html: e.target.value }))}
              rows={18}
              data-testid="wb-modal-html"
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono text-xs resize-y"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Placeholders: <code>&#123;&#123;min_deposit&#125;&#125;</code>, <code>&#123;&#123;bonus_amount&#125;&#125;</code>, <code>&#123;&#123;validity_days&#125;&#125;</code>, <code>&#123;&#123;total_balance&#125;&#125;</code>
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Live preview</span>
            <div
              data-testid="wb-modal-preview"
              className="p-5 border-2 border-dashed border-slate-300 rounded-lg bg-gradient-to-br from-violet-50 to-cyan-50 max-h-[420px] overflow-y-auto"
            >
              <h2 className="text-lg font-bold mb-3 text-violet-800">{data.modal_title || data.default_modal_title}</h2>
              <SafeHtml as="div" className="prose prose-sm max-w-none text-slate-700 [&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-2 [&_h3]:text-sm [&_h3]:font-bold [&_h3]:mt-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:my-1 [&_strong]:text-slate-900"
                /* Trusted admin content; preview-only sandbox. */ html={previewHtml} />
            </div>
          </div>
        </div>
      </div>

      {/* Email cross-link */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Mail className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <strong>Welcome-bonus email</strong> — subject and HTML body are managed in the
          <strong> Email </strong>tab → template <code>welcome_bonus</code>. Same placeholders
          (<code>&#123;&#123;min_deposit&#125;&#125;</code>, etc.) work there too. The email is
          sent automatically right after a fresh user verifies their email address.
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="wb-save"
          className="px-5 py-2.5 bg-gradient-to-r from-violet-500 to-cyan-500 text-white rounded-lg font-bold hover:shadow-lg disabled:opacity-50 transition-all flex items-center gap-2"
        >
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}
